import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { setting } from "../services";

// Initial state
const initialState = {
	users: [],
	selectedUser: null,
	loading: {
		users: false,
		create: false,
		update: false,
		delete: false,
	},
	error: {
		users: null,
		create: null,
		update: null,
		delete: null,
	},
};

// Async thunks
export const fetchUsers = createAsyncThunk(
	"users/fetchAll",
	async (_, { rejectWithValue }) => {
		try {
			const response = await setting.getUsers();
			return response?.items || [];
		} catch (error) {
			return rejectWithValue(error.message || "Failed to fetch users");
		}
	},
);

export const createUser = createAsyncThunk(
	"users/createUser",
	async (userData, { rejectWithValue }) => {
		try {
			const payload = {
				firstName: userData.firstName,
				lastName: userData.lastName,
				email: userData.email,
				phoneNumber: userData.phoneNumber,
				roleId: userData.role,
				companyId: userData.company,
			};

			if (userData.password) {
				payload.password = userData.password;
			}

			const response = await setting.addUser(payload);

			// Return the created user with properly structured data for the grid
			return {
				...response,
				id: response.id,
				firstName: response.firstName,
				lastName: response.lastName,
				email: response.email,
				phoneNumber: response.phoneNumber,
				role: {
					id: userData.role,
					name: response.role?.name || "Unknown",
				},
				company: {
					id: userData.company,
					name: response.company?.name || "Unknown",
				},
			};
		} catch (error) {
			return rejectWithValue(error.message || "Failed to create user");
		}
	},
);

export const updateUser = createAsyncThunk(
	"users/updateUser",
	async (userData, { rejectWithValue, getState }) => {
		try {
			const payload = {
				firstName: userData.firstName,
				lastName: userData.lastName,
				email: userData.email,
				phoneNumber: userData.phoneNumber,
				roleId: userData.role,
				companyId: userData.company,
			};

			if (userData.password) {
				payload.password = userData.password;
			}

			await setting.updateUser(payload, userData.id);

			// Find the role and company names from existing user data
			const { users } = getState().users;
			const existingUser = users.find((user) => user.id === userData.id);

			let roleName = "Unknown";
			let companyName = "Unknown";

			// Try to find existing role/company names from the updated user
			if (existingUser) {
				roleName = existingUser.role?.name || "Unknown";
				companyName = existingUser.company?.name || "Unknown";
			}

			// Return updated user with the same structure as in the state
			return {
				id: userData.id,
				firstName: userData.firstName,
				lastName: userData.lastName,
				email: userData.email,
				phoneNumber: userData.phoneNumber,
				role: {
					id: userData.role,
					name: roleName,
				},
				company: {
					id: userData.company,
					name: companyName,
				},
				createdAt: existingUser?.createdAt,
			};
		} catch (error) {
			return rejectWithValue(error.message || "Failed to update user");
		}
	},
);

export const deleteUser = createAsyncThunk(
	"users/deleteUser",
	async (userId, { rejectWithValue }) => {
		try {
			await setting.deleteUser(userId);
			return userId;
		} catch (error) {
			return rejectWithValue(error.message || "Failed to delete user");
		}
	},
);

// User slice
const userSlice = createSlice({
	name: "users",
	initialState,
	reducers: {
		setSelectedUser: (state, action) => {
			state.selectedUser = action.payload;
		},
		clearSelectedUser: (state) => {
			state.selectedUser = null;
		},
		clearErrors: (state) => {
			state.error = {
				users: null,
				create: null,
				update: null,
				delete: null,
			};
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch users
			.addCase(fetchUsers.pending, (state) => {
				state.loading.users = true;
				state.error.users = null;
			})
			.addCase(fetchUsers.fulfilled, (state, action) => {
				state.loading.users = false;
				state.users = action.payload;
			})
			.addCase(fetchUsers.rejected, (state, action) => {
				state.loading.users = false;
				state.error.users = action.payload;
			})

			// Create user
			.addCase(createUser.pending, (state) => {
				state.loading.create = true;
				state.error.create = null;
			})
			.addCase(createUser.fulfilled, (state, action) => {
				state.loading.create = false;
				state.users = [action.payload, ...state.users];
			})
			.addCase(createUser.rejected, (state, action) => {
				state.loading.create = false;
				state.error.create = action.payload;
			})

			// Update user
			.addCase(updateUser.pending, (state) => {
				state.loading.update = true;
				state.error.update = null;
			})
			.addCase(updateUser.fulfilled, (state, action) => {
				state.loading.update = false;
				const index = state.users.findIndex(
					(user) => user.id === action.payload.id,
				);
				if (index !== -1) {
					state.users[index] = action.payload;
				}
			})
			.addCase(updateUser.rejected, (state, action) => {
				state.loading.update = false;
				state.error.update = action.payload;
			})

			// Delete user
			.addCase(deleteUser.pending, (state) => {
				state.loading.delete = true;
				state.error.delete = null;
			})
			.addCase(deleteUser.fulfilled, (state, action) => {
				state.loading.delete = false;
				state.users = state.users.filter(
					(user) => user.id !== action.payload,
				);
			})
			.addCase(deleteUser.rejected, (state, action) => {
				state.loading.delete = false;
				state.error.delete = action.payload;
			});
	},
});

export const { setSelectedUser, clearSelectedUser, clearErrors } =
	userSlice.actions;

export default userSlice.reducer;
